﻿import type { SxProps, Theme } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  TApiApplicationDocument,
  TApiApplicationDocumentFilePayload,
  TApiApplicationDocumentPayload,
  TApiSubmitApplicationResponse,
  TCovenantsDocumentRowIdentitySource,
  TCovenantsDocumentStableKeySource,
  TApiRequiredDocument,
  TSignatureValidationState,
  StatusFilterValue,
  TLocalFileEntry,
  TLocalFiles,
} from '~/modules/covenants/types.ts';
import { DocStatusEnum, TDocument } from '~/modules/identification/forms/Documents/types.ts';
import {
  EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE,
  FILE_TOO_LARGE_ERROR_MESSAGE,
} from '~/modules/covenants/constants.tsx';
import {
  getAppStatusConfig,
  getRequiredDocumentStatusConfig,
  type IStatusConfig,
} from '~/modules/covenants/statusConfig.ts';
import { regularExpressions } from '~/constants/regularExpressions.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import { ERROR_CODES, HTTP_ERROR_STATUS } from '~/constants/errorCodes.ts';
import { getDecryptedCookieValue } from '~/helpers/cookieCrypto.ts';
import { safeWindowOpen } from '~/helpers/safeUrlOpen.ts';
import type { TIITHash } from '~/store/iit/types.ts';
import { MANAGER_USERNAME_COOKIE } from '../auth/constants';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getSavedManagerUsername = (): string | null => {
  return getDecryptedCookieValue(MANAGER_USERNAME_COOKIE);
};

export function getCurrentUserTimeZone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
}

export function formatDate(iso: string, format = 'DD.MM.YYYY'): string {
  return regularExpressions.ISO_TIMEZONE_SUFFIX.test(iso)
    ? formatDateInTimeZone(iso, format, getCurrentUserTimeZone())
    : dayjs(iso).format(format);
}

export function formatDateInTimeZone(
  iso: string,
  format = 'DD.MM.YYYY',
  timeZone?: string
): string {
  if (!timeZone) {
    return dayjs(iso).format(format);
  }

  return dayjs.utc(iso).tz(timeZone).format(format);
}

export const tableRowStyles = (style: SxProps<Theme>, theme: Theme): Record<string, unknown> => {
  if (!style) {
    return {};
  }

  if (typeof style === 'function') {
    return tableRowStyles(style(theme), theme);
  }

  if (Array.isArray(style)) {
    return style.reduce<Record<string, unknown>>((accumulator, item) => {
      if (!item) {
        return accumulator;
      }

      return {
        ...accumulator,
        ...tableRowStyles(item, theme),
      };
    }, {});
  }

  return style as Record<string, unknown>;
};

export const COVENANTS_APPLICATION_CONTRAGENT_QUERY_PARAM = 'contragentId';

export function parsePositiveNumber(value?: string | number | null): number {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
}

export function getCovenantsApplicationContragentStorageKey(applicationId: string): string {
  return `covenants:application:${applicationId}:contragentId`;
}

export function persistCovenantsApplicationContragent(
  applicationId: string,
  contragentId: number
): void {
  if (typeof window === 'undefined' || !applicationId) {
    return;
  }

  const normalizedContragentId = parsePositiveNumber(contragentId);

  if (!normalizedContragentId) {
    return;
  }

  window.sessionStorage.setItem(
    getCovenantsApplicationContragentStorageKey(applicationId),
    String(normalizedContragentId)
  );
}

export function getPersistedCovenantsApplicationContragent(applicationId?: string): number {
  if (typeof window === 'undefined' || !applicationId) {
    return 0;
  }

  return parsePositiveNumber(
    window.sessionStorage.getItem(getCovenantsApplicationContragentStorageKey(applicationId))
  );
}

export function buildCovenantsApplicationPath(
  applicationId: string | number,
  contragentId?: string | number | null
): string {
  const pathname = `/covenants/application/${applicationId}`;
  const normalizedContragentId = parsePositiveNumber(contragentId);

  if (!normalizedContragentId) {
    return pathname;
  }

  const searchParams = new URLSearchParams({
    [COVENANTS_APPLICATION_CONTRAGENT_QUERY_PARAM]: String(normalizedContragentId),
  });

  return `${pathname}?${searchParams.toString()}`;
}

export function buildCovenantsManagerApplicationPath(
  applicationId: string | number,
  search?: string
): string {
  const pathname = `/covenants/manager/applications/${applicationId}`;

  if (!search) {
    return pathname;
  }

  return `${pathname}${search.startsWith('?') ? search : `?${search}`}`;
}
export function buildApplicationDocumentStableKey(doc: TCovenantsDocumentStableKeySource): string {
  return `${doc.documentId}_${doc.yearPeriod || ''}_${doc.childPeriod || ''}_${doc.deadline || ''}`;
}

export function getRowId(doc: TCovenantsDocumentStableKeySource): string {
  return `${buildApplicationDocumentStableKey(doc)}__virtual__${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function buildApplicationDocumentRowKey(doc: TApiApplicationDocument): string {
  if (doc.rowId) {
    return doc.rowId;
  }

  const stableKey = buildApplicationDocumentStableKey(doc);

  if (doc.id) {
    return `${stableKey}__${doc.id}`;
  }

  return `${stableKey}__required`;
}

const buildApplicationDocumentPayloadRowId = (
  payload: TCovenantsDocumentRowIdentitySource,
  fileIndex?: number
): string => {
  const stableKey = buildApplicationDocumentStableKey(payload);

  const parts = [
    stableKey,
    payload.id ?? 'no-id',
    payload.createdAt ?? 'no-created-at',
    payload.fileName ?? 'no-file-name',
    fileIndex !== undefined ? String(fileIndex) : undefined,
  ].filter(Boolean);

  return parts.join('__');
};

const buildNormalizedApplicationDocument = (
  document: TApiApplicationDocumentPayload,
  override: Partial<TApiApplicationDocument> = {}
): TApiApplicationDocument => ({
  version: document.version ?? 0,
  id: document.id,
  rowId: buildApplicationDocumentPayloadRowId(document),
  documentName: document.documentName,
  documentId: String(document.documentId),
  fileName: document.fileName,
  comment: document.comment,
  createdAt: document.createdAt,
  deadline: document.deadline,
  status: document.status ?? StatusFilterValue.New,
  isCustomSignFile: !!document.isCustomSignFile,
  hasSignedFile: document.hasSignedFile,
  requiredDocumentStatus: document.requiredDocumentStatus,
  yearPeriod: document.yearPeriod,
  childPeriod: document.childPeriod,
  signedByName: document.signedByName,
  signetByCode: document.signetByCode,
  ...override,
});

const compareApplicationDocumentFiles = (
  left: TApiApplicationDocumentFilePayload,
  right: TApiApplicationDocumentFilePayload
): number => {
  const leftTimestamp = left.createdAt ? dayjs(left.createdAt).valueOf() : Number.NEGATIVE_INFINITY;
  const rightTimestamp = right.createdAt
    ? dayjs(right.createdAt).valueOf()
    : Number.NEGATIVE_INFINITY;

  if (leftTimestamp !== rightTimestamp) {
    return leftTimestamp - rightTimestamp;
  }

  return (left.fileName ?? '').localeCompare(right.fileName ?? '');
};

export function normalizeApplicationDocuments(
  document: TApiApplicationDocumentPayload
): TApiApplicationDocument[] {
  const files = Array.isArray(document.files) ? document.files : null;

  if (!files?.length) {
    return [buildNormalizedApplicationDocument(document)];
  }

  return [...files]
    .sort(compareApplicationDocumentFiles)
    .map((file: TApiApplicationDocumentFilePayload, index: number) =>
      buildNormalizedApplicationDocument(document, {
        id: file.id ?? document.id,
        fileId: file.fileId,
        rowId: buildApplicationDocumentPayloadRowId(
          {
            documentId: document.documentId,
            yearPeriod: document.yearPeriod,
            childPeriod: document.childPeriod,
            deadline: document.deadline,
            id: file.id ?? document.id,
            fileName: file.fileName,
            createdAt: file.createdAt,
          },
          index
        ),
        fileName: file.fileName,
        comment: file.comment,
        createdAt: file.createdAt,
        status: file.status ?? document.status ?? StatusFilterValue.New,
        isCustomSignFile: !!file.isCustomSignFile,
        hasSignedFile: !!file.hasSignedFile || !!file.isCustomSignFile,
        signedByName: file.signedByName ?? document.signedByName,
        signetByCode: file.signetByCode ?? document.signetByCode,
        availableToRegenerate: file.availableToRegenerate ?? document.availableToRegenerate,
      })
    );
}

export function mergeApplicationDocumentFilePayload(
  document: TApiApplicationDocument,
  filePayload: TApiApplicationDocumentFilePayload
): TApiApplicationDocument {
  const computedRowId = buildApplicationDocumentPayloadRowId({
    documentId: document.documentId,
    yearPeriod: document.yearPeriod,
    childPeriod: document.childPeriod,
    deadline: document.deadline,
    id: filePayload.id ?? document.id,
    fileName: filePayload.fileName,
    createdAt: filePayload.createdAt,
  });

  return {
    ...document,
    isVirtualRow: false,
    id: filePayload.id ?? document.id,
    fileId: filePayload.fileId ?? document.fileId,
    rowId: document.rowId ?? computedRowId,
    fileName: filePayload.fileName,
    comment: filePayload.comment,
    createdAt: filePayload.createdAt,
    status: filePayload.status ?? document.status ?? StatusFilterValue.New,
    isCustomSignFile: filePayload.isCustomSignFile ?? document.isCustomSignFile,
    hasSignedFile: filePayload.hasSignedFile ?? document.hasSignedFile,
    signedByName: filePayload.signedByName ?? document.signedByName,
    signetByCode: filePayload.signetByCode ?? document.signetByCode,
    availableToRegenerate: filePayload.availableToRegenerate ?? document.availableToRegenerate,
  };
}

export const getUploadFileExtension = (fileName: string): string => {
  const normalizedFileName = fileName.trim().toLowerCase();
  const extensionSeparatorIndex = normalizedFileName.lastIndexOf('.');

  if (extensionSeparatorIndex <= 0 || extensionSeparatorIndex === normalizedFileName.length - 1) {
    return '';
  }

  return normalizedFileName.slice(extensionSeparatorIndex + 1);
};

export const isAllowedUploadFileExtension = (file: File, allowedExtensions?: string[]): boolean => {
  if (!allowedExtensions) {
    return true;
  }

  const extension = getUploadFileExtension(file.name);

  return allowedExtensions.some((allowedExtension) => allowedExtension.toLowerCase() === extension);
};

export const isApplicationVersionMismatch = (error: unknown): boolean => {
  const errorData = error as
    | {
        code?: string;
        errorCode?: string;
        data?: { code?: string; errorCode?: string };
      }
    | undefined;
  const code =
    errorData?.code ?? errorData?.errorCode ?? errorData?.data?.code ?? errorData?.data?.errorCode;

  return code === 'APPLICATION_VERSION_MISMATCH';
};

export const isFileTooLargeError = (error: unknown): boolean => {
  const normalizedError = error as
    | {
        status?: number;
        message?: string;
        data?: {
          status?: number;
          message?: string;
        };
      }
    | undefined;

  const status = normalizedError?.status ?? normalizedError?.data?.status;
  const message = (normalizedError?.data?.message ?? normalizedError?.message ?? '').toLowerCase();

  return status === 413 || message.includes('too large');
};

export const getUploadErrorMessage = (error: unknown): string => {
  if (isFileTooLargeError(error)) {
    return FILE_TOO_LARGE_ERROR_MESSAGE;
  }

  const message =
    (
      error as
        | {
            message?: string;
            data?: {
              message?: string;
            };
          }
        | undefined
    )?.data?.message ?? (error as { message?: string } | undefined)?.message;

  return message || 'Не вдалося завантажити файл. Спробуйте ще раз.';
};

export const buildPendingUploadDocument = (
  document: TApiApplicationDocument
): TApiApplicationDocument => ({
  ...document,
  id: undefined,
  rowId: getRowId(document),
  isVirtualRow: true,
  fileName: undefined,
  comment: undefined,
  createdAt: undefined,
  isCustomSignFile: false,
  hasSignedFile: false,
  signedByName: undefined,
  signetByCode: undefined,
  availableToRegenerate: false,
  status: StatusFilterValue.New,
});
export function buildDocumentsForIITSigning(
  documents: TApiApplicationDocument[],
  iitHashes: TIITHash[],
  localFiles: TLocalFiles
): TDocument[] {
  const buildIITDocumentDedupKey = (document: TApiApplicationDocument): string => {
    if (document.id) {
      return `id:${document.id}`;
    }

    return `fallback:${buildApplicationDocumentRowKey(document)}`;
  };

  const uniqueDocuments = Array.from(
    new Map(documents.map((document) => [buildIITDocumentDedupKey(document), document])).values()
  );
  const rowKeyToStableIdDoc = new Map(
    uniqueDocuments
      .map((document) => buildApplicationDocumentRowKey(document))
      .sort((left, right) => left.localeCompare(right))
      .map((rowKey, index) => [rowKey, index + 1])
  );
  const hashesByDocumentId = iitHashes.reduce<Record<string, string[]>>((acc, hash) => {
    const documentId = String(hash.documentId);

    if (!acc[documentId]) {
      acc[documentId] = [];
    }

    acc[documentId].push(hash.fileNameToSign);
    return acc;
  }, {});
  const hashIndexesByDocumentId: Record<string, number> = {};

  return uniqueDocuments.map((doc) => {
    const rowKey = buildApplicationDocumentRowKey(doc);
    const documentId = String(doc.documentId);
    const currentHashIndex = hashIndexesByDocumentId[documentId] || 0;
    const iitFileName = hashesByDocumentId[documentId]?.[currentHashIndex];
    const localFileName = localFiles[rowKey]?.file?.name;
    const displayName = iitFileName || localFileName || doc.fileName || doc.documentName;

    hashIndexesByDocumentId[documentId] = currentHashIndex + 1;

    return {
      id: rowKey,
      nameDoc: displayName,
      idDictWebDocument: Number(doc.documentId),
      idDoc: rowKeyToStableIdDoc.get(rowKey) ?? 0,
      docStatus: [DocStatusEnum.NOT_VERIFIED],
      documentName: displayName,
    };
  });
}

export function findApplicationDocumentByPreviewId(
  documents: TApiApplicationDocument[],
  previewId: number | string
): TApiApplicationDocument | undefined {
  const normalizedPreviewId = String(previewId);

  return (
    documents.find((doc) => buildApplicationDocumentRowKey(doc) === normalizedPreviewId) ||
    documents.find((doc) => buildApplicationDocumentStableKey(doc) === normalizedPreviewId) ||
    documents.find((doc) => String(doc.documentId) === normalizedPreviewId)
  );
}

export function getCovenantsRequestErrorMessage(error: unknown): string {
  return (
    (error as { message?: string; data?: { message?: string } } | undefined)?.message ||
    (error as { data?: { message?: string } } | undefined)?.data?.message ||
    errorMessages.SERVICE_UNAVAILABLE
  );
}

export function resolveCovenantsRequestErrorMessage(error: unknown): string {
  const normalizedError = error as
    | {
        status?: string | number;
        code?: string | number;
        errorCode?: string | number;
        message?: string;
        data?: {
          status?: string | number;
          code?: string | number;
          errorCode?: string | number;
          message?: string;
        };
      }
    | undefined;
  const errorStatus = Number(normalizedError?.status ?? normalizedError?.data?.status);
  const errorCode = Number(
    normalizedError?.data?.errorCode ??
      normalizedError?.errorCode ??
      normalizedError?.data?.code ??
      normalizedError?.code
  );
  const backendMessage = normalizedError?.data?.message;
  const fallbackMessage = getCovenantsRequestErrorMessage(error);

  if (
    errorStatus === HTTP_ERROR_STATUS.CONFLICT &&
    errorCode === ERROR_CODES.MULTI_SIGNERS_NOT_SUPPORTED &&
    backendMessage
  ) {
    return backendMessage;
  }

  if (errorCode === ERROR_CODES.DOCUMENTS_LIMIT_EXCEEDED && backendMessage) {
    return backendMessage;
  }

  return fallbackMessage;
}

function normalizeMimeType(contentType?: string | null): string {
  return contentType?.split(';')[0]?.trim().toLowerCase() ?? '';
}

export function isPdfFile(fileName?: string | null, contentType?: string | null): boolean {
  const normalizedFileName = (fileName ?? '').trim().toLowerCase();
  const fileExtension = normalizedFileName.includes('.')
    ? normalizedFileName.split('.').pop()
    : undefined;

  if (fileExtension) {
    return fileExtension === 'pdf';
  }

  return normalizeMimeType(contentType) === 'application/pdf';
}

export function openOrDownloadBlob({
  blob,
  fileName,
  fallbackFileName,
  contentType,
  forceDownload = false,
}: {
  blob: Blob;
  fileName?: string | null;
  fallbackFileName: string;
  contentType?: string | null;
  forceDownload?: boolean;
}): void {
  const normalizedContentType = normalizeMimeType(contentType);
  const normalizedBlob =
    !blob.type && normalizedContentType ? blob.slice(0, blob.size, normalizedContentType) : blob;
  const resolvedFileName = fileName?.trim() || fallbackFileName;
  const url = window.URL.createObjectURL(normalizedBlob);

  if (!forceDownload && isPdfFile(resolvedFileName, normalizedBlob.type || normalizedContentType)) {
    if (safeWindowOpen(url, { base: window.location.origin })) {
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      return;
    }
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = resolvedFileName;
  link.click();

  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export function getSignatureValidationMessages(response: TApiSubmitApplicationResponse): string[] {
  const messages: string[] = [];

  if (!!(response.documentsWithInvalidSignatures || []).length) {
    messages.push('Деякі підписи не дійсні, перевірте їх та спробуйте ще раз');
  }

  if (!!(response.documentsWithRNOCPPMismatch || []).length) {
    messages.push(
      'Під час перевірки прав підпису виявлено, що у Вас відсутні права. Необхідно звернутися до керівника компанії із запитом щодо підписання документів'
    );
  }

  if (!!(response.noKepDocuments || []).length) {
    messages.push('Деякі підписи не є КЕП');
  }

  return messages;
}

export function hasSignatureValidationIssues(response: TApiSubmitApplicationResponse): boolean {
  return !!getSignatureValidationMessages(response).length;
}

export function buildSignatureValidationState(
  response: TApiSubmitApplicationResponse,
  documents: TApiApplicationDocument[] = []
): TSignatureValidationState {
  const nextState: TSignatureValidationState = {};

  const ensureDocumentState = (documentKey: string) => {
    if (!nextState[documentKey]) {
      nextState[documentKey] = { ...EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE };
    }

    return nextState[documentKey];
  };

  const getRelatedDocumentKeys = (documentId: string | number): string[] => {
    const normalizedDocumentId = String(documentId);
    const relatedDocument = documents.find(
      (doc) =>
        String(doc.documentId) === normalizedDocumentId || String(doc.id) === normalizedDocumentId
    );

    return Array.from(
      new Set(
        [
          normalizedDocumentId,
          relatedDocument?.documentId ? String(relatedDocument.documentId) : undefined,
          relatedDocument?.id ? String(relatedDocument.id) : undefined,
        ].filter((key): key is string => !!key)
      )
    );
  };

  const updateDocumentState = (
    documentId: string | number,
    update: (documentState: (typeof nextState)[string]) => void
  ) => {
    getRelatedDocumentKeys(documentId).forEach((documentKey) => {
      update(ensureDocumentState(documentKey));
    });
  };

  (response.documentsWithInvalidSignatures || []).forEach((documentId: string) => {
    updateDocumentState(documentId, (documentState) => {
      documentState.hasInvalidSignature = true;
    });
  });

  (response.documentsWithRNOCPPMismatch || []).forEach((documentId: string) => {
    updateDocumentState(documentId, (documentState) => {
      documentState.hasRNOCPPMismatch = true;
    });
  });

  (response.noKepDocuments || []).forEach((documentId: string) => {
    updateDocumentState(documentId, (documentState) => {
      documentState.hasNoKep = true;
    });
  });

  (response.generatedProtocols || []).forEach((documentId: string) => {
    updateDocumentState(documentId, (documentState) => {
      documentState.hasGeneratedProtocol = true;
    });
  });

  return nextState;
}

export function getDocumentStatusConfig(status: TApiRequiredDocument['status']): IStatusConfig {
  return getRequiredDocumentStatusConfig(status);
}

export { getAppStatusConfig };
export type { IStatusConfig };
export const buildApplicationSubmittedModal = (onCloseCallback: () => void) => ({
  open: true,
  type: 'success' as const,
  message: 'Заявку успішно надіслано!',
  onCloseCallback,
});
export const buildStatusErrorModal = (message: string) => ({
  open: true,
  type: 'error' as const,
  message,
});

export const buildSignatureQrTimeoutModal = () =>
  buildStatusErrorModal(errorMessages.SIGNATURE_QR_CODE_TIME_OUT);

export const buildNoFilesModal = () => ({
  open: true,
  type: 'error' as const,
  title: 'Увага',
  message: 'Будь ласка, завантажте хоча б один документ перед надсиланням заявки.',
});

export const buildNoDocumentsModal = () => ({
  open: true,
  type: 'error' as const,
  title: 'Увага',
  message: 'У цій заявці немає документів для подання. Надіслати порожню заявку неможливо.',
});

const isEditableApplicationDocumentStatus = (status: string): boolean => {
  return status === 'NEW';
};

const hasDocumentFile = (doc: TApiApplicationDocument, localFile?: TLocalFileEntry): boolean => {
  return !!doc.fileName || !!localFile?.file;
};

const hasSignatureFile = (localFile?: TLocalFileEntry): boolean => {
  return !!localFile?.signature || !!localFile?.isExternal;
};

export const buildApplicationVersionMismatchModal = (onClick: () => void) => ({
  open: true,
  type: 'error' as const,
  title: 'Заявку змінено',
  message: 'Заявку вже оновив хтось інший. Оновіть, будь ласка, вікно.',
  buttonValue: 'Оновити',
  onClick,
});

export function canAttachSignature(
  doc: TApiApplicationDocument,
  localFile?: TLocalFileEntry,
  signatureValidationState = EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE
): boolean {
  const hasSignatureIssue =
    signatureValidationState.hasInvalidSignature ||
    signatureValidationState.hasRNOCPPMismatch ||
    signatureValidationState.hasNoKep;
  const hasExistingSignature =
    doc.isCustomSignFile || !!doc.hasSignedFile || hasSignatureFile(localFile);
  const canAttachForSignatureIssue =
    hasSignatureIssue && (!signatureValidationState.hasGeneratedProtocol || !hasExistingSignature);

  return (
    isEditableApplicationDocumentStatus(doc.status) &&
    hasDocumentFile(doc, localFile) &&
    (canAttachForSignatureIssue ||
      (!signatureValidationState.hasGeneratedProtocol && !hasExistingSignature))
  );
}

export function canDeleteSignature(
  doc: TApiApplicationDocument,
  localFile?: TLocalFileEntry,
  signatureValidationState = EMPTY_SIGNATURE_VALIDATION_DOCUMENT_STATE
): boolean {
  const hasSignatureIssue =
    signatureValidationState.hasInvalidSignature ||
    signatureValidationState.hasRNOCPPMismatch ||
    signatureValidationState.hasNoKep;

  return (
    !signatureValidationState.hasGeneratedProtocol &&
    !hasSignatureIssue &&
    isEditableApplicationDocumentStatus(doc.status) &&
    (hasSignatureFile(localFile) || doc.isCustomSignFile)
  );
}

export function canDeleteFile(doc: TApiApplicationDocument, localFile?: TLocalFileEntry): boolean {
  return isEditableApplicationDocumentStatus(doc.status) && hasDocumentFile(doc, localFile);
}
